import React, {Fragment, FunctionComponent, StrictMode} from "react";

import {Checkbox} from "@App/Components/common/Checkbox";
import {Garage, Organization, Service} from "@App/types";
import {usePremiumServices} from "@App/hooks/api/usePremiumServices";
import {
    OrganizationIsLeaseplanAndServiceIsAppointment,
    ServiceIsWheelRelatedAndOrganizationIsMeinAuto,
    ServiceIsWheelRelatedAndOrganizationIsMobilityConcept
} from "@App/utils/conditionals/combinations";
import {useTranslation} from "react-i18next";
import {
    PremiumServiceIsCollectAndReturnId,
    PremiumServiceIsReplacementVehicleId,
    ServiceIsGlass
} from "@App/utils/conditionals/services";
import {GarageHasNationwideService} from "@App/utils/conditionals/garages";
import {usePremiumServiceUtils} from "@App/hooks/usePremiumServiceUtils";
import {OrganizationIsArval, OrganizationIsAthlon, OrganizationIsAyvens} from "@App/utils/conditionals/organization";
import {PremiumServiceIsReplacementOrPickup,} from "@App/utils/conditionals/premiumServices";
import {useDriverData} from "@App/hooks/useDriverData";

type PremiumServiceSelectorProps = {
    organization: Organization,
    service: Service,
    garage: Garage
}

export const PremiumServiceSelector: FunctionComponent<PremiumServiceSelectorProps> = (props: PremiumServiceSelectorProps) => {
    const {t} = useTranslation()
    const {loading, premiumServices: availablePremiumServices, error} = usePremiumServices()

    const {
        togglePremiumService,
        togglePremiumServiceCarReplacementOrPickup,
        toggled,
        toggleSingularPremiumService
    } = usePremiumServiceUtils()

    const {
        createRequiredServiceArray,
        createRequiredPremiumserviceArray,
        createOptionalPremiumserviceArray,
        driverResponse,
    } = useDriverData()


    if (loading) {
        return <></>
    }

    if (error) {
        // log to sentry
    }

    const comfortServices = (premiumServices) => {
        if(!premiumServices) {
            return [];
        }
        return  premiumServices.filter(premiumService => premiumService.service_type === 'comfort-service');
    }

    const subsServices = (premiumServices) => {
        if(!premiumServices) {
            return [];
        }
       return  premiumServices.filter(premiumService => premiumService.service_type === 'sub-service');
    }

    return (
        <StrictMode>
            <Fragment>
                {OrganizationIsAyvens(props.organization) && (
                    <Fragment>
                        <div className="font-bold text-sm mb-3">{t('driverDataStep.mandatory')}</div>
                        {createRequiredServiceArray(driverResponse.serviceCatalog.required?.services).map((service) => (
                            <Checkbox key={service.id} status={true}
                                      title={service.name}
                                      onChange={() => {
                                          return
                                      }}
                                      disabled={true}/>
                        ))}
                        {createRequiredPremiumserviceArray(driverResponse.serviceCatalog.required?.premiumServices).map((service) => (
                            <Checkbox key={service.id} status={true}
                                      title={service.name}
                                      onChange={() => {
                                          return
                                      }}
                                      disabled={true}/>
                        ))}
                        <div className="font-bold text-sm mb-3 mt-8">{t('driverDataStep.optional')}</div>
                        {createOptionalPremiumserviceArray(driverResponse.serviceCatalog.optional?.premiumServices).map((service) => (
                            <Checkbox key={service.id} status={toggled(service)}
                                      title={service.name}
                                      onChange={() => togglePremiumService(service)}
                            />
                        ))}

                        {comfortServices(availablePremiumServices)?.length > 0 && (
                            <div className={`font-bold text-sm mb-3 ${subsServices?.length > 0 ? 'mt-8' : ''}`}>{comfortServices(availablePremiumServices).find(premiumService => premiumService.paid) ? t('premiumServices.additionalServices') :  t('premiumServices.additionalServicesNoStar')}</div>
                        )}
                        {comfortServices(availablePremiumServices)?.map((premiumService) => (
                            <Fragment key={'fragment-' + premiumService.id}>
                                <Checkbox key={premiumService.id} status={toggled(premiumService)}
                                          title={premiumService.name + (premiumService.paid ? '*' : '')}
                                          onChange={() =>
                                              PremiumServiceIsReplacementOrPickup(premiumService) ?
                                                  togglePremiumServiceCarReplacementOrPickup(premiumService) :
                                                  togglePremiumService(premiumService)}
                                />
                            </Fragment>
                        ))}
                    </Fragment>
                )}

            </Fragment>
            {!OrganizationIsAyvens(props.organization) && (
                <Fragment>

            {subsServices(availablePremiumServices).length > 0 && (
                <p className={'mb-2'}>{t('booking.please-choose-services')}</p>
            )}
            {availablePremiumServices && subsServices(availablePremiumServices).length > 0 && subsServices(availablePremiumServices).map(premiumService => (
                <Fragment key={'fragment-' + premiumService.id}>
                        <Checkbox key={premiumService.id} status={toggled(premiumService)}
                                  title={premiumService.name + (premiumService.paid ? '*' : '')}
                                  onChange={OrganizationIsAthlon(props.organization) ? () => toggleSingularPremiumService(premiumService) : () => togglePremiumService(premiumService)}/>
                </Fragment>
            ))}
            {subsServices(availablePremiumServices).length > 0 && availablePremiumServices && comfortServices(availablePremiumServices).length > 0 &&(
                <p className={'mb-2 mt-6'}>Zusatzservices</p>
            )}
            {availablePremiumServices && comfortServices(availablePremiumServices).length > 0 && comfortServices(availablePremiumServices).map(premiumService => (
                <Fragment key={'fragment-' + premiumService.id}>
                    <div className={'tooltipContainer'}>
                        {PremiumServiceIsReplacementOrPickup(premiumService) && (
                            <Checkbox key={premiumService.id} status={toggled(premiumService)}
                                      title={premiumService.name + (premiumService.paid ? '*' : '')}
                                      onChange={() => togglePremiumServiceCarReplacementOrPickup(premiumService)}/>
                        )}
                        {!PremiumServiceIsReplacementOrPickup(premiumService) && (
                            <Checkbox key={premiumService.id} status={toggled(premiumService)}
                                      title={premiumService.name + (premiumService.paid ? '*' : '')}
                                      onChange={() => togglePremiumService(premiumService)}/>
                        )}
                        {(OrganizationIsArval(props.organization) && PremiumServiceIsCollectAndReturnId(premiumService)) &&(
                            <span className="tooltipContent toolTipBooking hidden rounded-md">{t('listHints.collectAndReturnTooltip')}</span>
                        )}
                        {(OrganizationIsArval(props.organization) && PremiumServiceIsReplacementVehicleId(premiumService)) &&(
                            <span className="tooltipContent toolTipBooking hidden rounded-md">{t('listHints.replacementVehicleTooltip')}</span>
                        )}
                    </div>
                </Fragment>
            ))}


            {OrganizationIsLeaseplanAndServiceIsAppointment(props.organization, props.service) && (
                <div className={'bg-white rounded-md mt-4 px-4 py-3 text-neutral-700'}>
                    <p className={"text-sm"}><b>Ersatzwagen:</b> Bitte kontaktieren Sie LeasePlan nach der Werkstattbuchung unter <a href={t('hotline.hotline-phone-link')}
                                                                                                                                     className={'text-neutral-700 no-underline font-normal'}>{t('hotline.hotline-phone')}</a> (Mo
                        - Do 8:00 - 18:00 Uhr und Fr 8:00 - 17:00 Uhr)
                    </p>
                </div>
            )}

            {ServiceIsGlass(props.service) && GarageHasNationwideService(props.garage) &&(
                <div className={'bg-white rounded-md mt-4 px-4 py-3 text-neutral-700'}>
                    <p className={"text-sm"}>{t('bookingInformation.mobileGlassDisclaimer')}</p>
                </div>
            )}

            {availablePremiumServices && availablePremiumServices.find(premiumService => premiumService.paid) && (
                <p className={"text-sm mt-5"}>
                    {ServiceIsWheelRelatedAndOrganizationIsMeinAuto(props.organization, props.service) &&
                        t('garageList.filters.premiumServices.additional-cost-infoTireStorageDetail')
                    }
                    {ServiceIsWheelRelatedAndOrganizationIsMobilityConcept(props.organization, props.service) &&
                        t('garageList.filters.premiumServices.additional-cost-mightNotBeInContract')
                    }
                    {(!ServiceIsWheelRelatedAndOrganizationIsMobilityConcept(props.organization, props.service) && !ServiceIsWheelRelatedAndOrganizationIsMeinAuto(props.organization, props.service)) &&
                        t('garageList.filters.premiumServices.additional-cost-info')
                    }
                </p>
            )}
                </Fragment>
            )}
        </StrictMode>
    );
}
